import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotesIcon from '@mui/icons-material/Notes';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DescriptionIcon from '@mui/icons-material/Description';
import EventHeader from '../../../components/ui/events/EventHeader';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';

/**
 * MatchEventRow - Renders a single match event
 *
 * Optimized with React.memo to prevent unnecessary re-renders when:
 * - New events are added to the top of the list
 * - Other events are updated
 * - Parent components re-render
 *
 * Only re-renders when:
 * - This specific event's data changes
 * - Location address updates
 */
const MatchEventRow = React.memo(function MatchEventRow({
  event,
  isChild = false,
  address,
  onLocationClick,
  onCreateStatement
}) {
  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata || !event.metadata?.personName) {
    return null;
  }

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const noteCount = event.metadata?.notes?.length || 0;
  const timestamp = new Date(event.timestamp || event.createdAt);
  const sightingCount = event.metadata?.sightingCount !== undefined ? event.metadata.sightingCount : null;

  // Get color for processing elapsed time
  const getElapsedTimeColor = (ms) => {
    if (ms < 500) return '#4caf50'; // Green
    if (ms < 750) return '#ff9800'; // Amber
    if (ms >= 1500) return '#f44336'; // Red
    return '#ff9800'; // Amber for 750-1499ms range
  };

  // Determine interest reason based on event type
  const getInterestReason = () => {
    // Unified schema: read from interest.reason
    const interestReason = event.metadata?.person?.interest?.reason;
    if (interestReason) {
      return interestReason;
    }
    // Derive from event type if no person record
    if (event.eventType === 'soi_match') {
      return 'Subject of Interest';
    }
    return 'Associated Person';
  };

  // Determine background color based on relationship
  const backgroundColor = isChild ? '#f5f5f5' : '#fff';
  const borderColor = isChild ? '4px solid #ffcc80' : 'none';

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title="Match">
          <PersonSearchIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty for matches) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EventHeader
              personImage={event.metadata.personImage}
              personName={event.metadata.personName}
              username=""
              matchSource={event.metadata.matchSource}
              capturedImageSignedUrl={capturedImageUrl}
              capturedImageUrl={event.metadata?.capturedImageUrl}
              loadingCapturedImage={false}
              matchAccuracy={event.metadata.matchAccuracy}
              eventLabel=""
              personUri={event.metadata?.person?.uri}
              boundingBox={event.metadata?.boundingBox}
              interestLevel={event.metadata?.person?.interest?.level || 'LOW'}
              interestReason={getInterestReason()}
              onPersonImageClick={() => {
                if (event.metadata?.person?.uri) {
                  window.open(event.metadata.person.uri, '_blank', 'noreferrer');
                }
              }}
              onCapturedImageClick={() => {
                if (capturedImageUrl) {
                  window.open(capturedImageUrl, '_blank', 'noreferrer');
                }
              }}
            />
          </Box>
          {noteCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {noteCount}
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Timestamp */}
      <TableCell sx={{ width: 140 }}>
        <Typography variant="caption">
          {timestamp.toLocaleString()}
        </Typography>
        {event.metadata?.processingElapsedMs !== undefined && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              color: getElapsedTimeColor(event.metadata.processingElapsedMs),
              fontWeight: 500
            }}
          >
            +{event.metadata.processingElapsedMs}ms
          </Typography>
        )}
      </TableCell>

      {/* User Account */}
      <TableCell sx={{ width: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {event.userHandle || event.userEmail || event.userId || '—'}
        </Typography>
      </TableCell>

      {/* Location */}
      <TableCell>
        {event.location ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onLocationClick(event);
              }}
              sx={{ p: 0.5 }}
            >
              <LocationOnIcon fontSize="small" color="primary" />
            </IconButton>
            <Typography variant="caption">
              {address || `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption">N/A</Typography>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell align="center" sx={{ width: 60 }}>
        {onCreateStatement && (
          <Tooltip title="Create Statement">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCreateStatement(event);
              }}
              sx={{ p: 0.5 }}
            >
              <DescriptionIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if these specific props changed
  return (
    prevProps.event.eventId === nextProps.event.eventId &&
    prevProps.event.metadata?.notes?.length === nextProps.event.metadata?.notes?.length &&
    prevProps.isChild === nextProps.isChild &&
    prevProps.address === nextProps.address
  );
});

export default MatchEventRow;
